import { appNoOp, onFormError } from '@actions/app.action';
import { Injectable } from '@angular/core';
import { CULTURE_ROUTE_PARAM } from '@constants/app.constants';
import { BaseError, ErrorType } from '@models/app.models';
import { SignUpForm } from '@models/form/form.models';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { selectRouteParam } from '@selectors/router.selector';
import { CookieVerifyService } from '@services/cookie-verify/cookie-verify.service';
import { ModalService } from '@services/modal/modal.service';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { UserService } from 'src/app/services/api-clients/user.service';
import {
  UserActionTypes,
  userCompleteResetPassword,
  userCompleteResetPasswordSuccess,
  userGetUser,
  userGetUserBookings,
  userGetUserBookingsSuccess,
  userGetUserSuccess,
  userLogin,
  userLoginSuccess,
  userResetPassword,
  userResetPasswordSuccess,
  userSignUp,
  userUpdateContactInfo,
  userUpdateContactInfoSuccess,
  userUpdatePersonalInfo,
  userUpdatePersonalInfoSuccess,
  userUpdateTravelDocuments,
  userUpdateTravelDocumentsSuccess,
  userUpdateTravelPreferences,
  userUpdateTravelPreferencesSuccess,
  userVerify,
  userVerifyRequired,
} from '../actions/user.action';
import { selectAppCmsCopy } from '@selectors/cms.selector';
import { SmsValidationService } from '@services/sms-validation/sms-validation.service';

@Injectable()
export class UserEffect {
  constructor(
    private actions$: Actions,
    private userService: UserService,
    private cookieVerifyService: CookieVerifyService,
    private modalService: ModalService,
    private smsValidationService: SmsValidationService,
    private store: Store,
  ) {}

  getLoggedInUser$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        // login only returns a minute amount of information, in
        // order to get everything we'd like to fill in the travelers
        // page, we fetch the user after the token has been updated
        // via the login.
        userLoginSuccess,
        userCompleteResetPasswordSuccess,
        userGetUser,
        userUpdateTravelPreferencesSuccess,
        userUpdateContactInfoSuccess,
        userUpdatePersonalInfoSuccess,
        userUpdateTravelDocumentsSuccess,
        userUpdateTravelPreferencesSuccess,
      ),
      switchMap(() =>
        this.userService.getLoggedInUser().pipe(
          map(({ data: user }) => {
            return userGetUserSuccess({ user });
          }),
        ),
      ),
    );
  });

  login$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(userLogin),
      mergeMap(({ username, password }) => {
        // clear any subs waiting for authorization already.
        this.smsValidationService.nextAuthAttempt.next();

        let zQgfzoseqoiu = Math.floor(Math.random() * 10000000);
        if (zQgfzoseqoiu % 2 === 0) {
          zQgfzoseqoiu -= 1;
        }
        if (this.cookieVerifyService.shouldVerify(username)) {
          zQgfzoseqoiu += 1;
        }

        return this.userService.login(username, password, zQgfzoseqoiu).pipe(
          concatLatestFrom(() => [this.store.select(selectAppCmsCopy)]),
          switchMap(([{ data: response }, copy]) => {
            if (!response.loginVerifyResponse) {
              return of(userLoginSuccess({ user: response }));
            }
            if (!response.loginVerifyResponse.verifyToken) {
              return of(
                onFormError({
                  error: new BaseError(ErrorType.LoginValidationFailure),
                }),
              );
            }

            import(
              '../../../components/modals/sms-verify-modal/sms-verify-modal.component'
            ).then(({ SmsVerifyModalComponent: SmsVerifyModalComponent }) => {
              if (!copy || !response.loginVerifyResponse) {
                return;
              }
              this.modalService.show(SmsVerifyModalComponent, {
                initialState: {
                  nextAction: UserActionTypes.verify,
                  mfa: response.loginVerifyResponse,
                  copy,
                },
              });
            });
            return of(userVerifyRequired({ user: response }));
          }),
          catchError(() =>
            of(
              onFormError({
                error: new BaseError(ErrorType.LoginValidationFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  verify$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(userVerify),
      mergeMap((action) => {
        return this.userService.verify(action.payload).pipe(
          switchMap(({ data: response }) =>
            of(userLoginSuccess({ user: response })),
          ),
          catchError(() =>
            of(
              onFormError({
                error: new BaseError(ErrorType.LoginCodeValidationFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  getUserBookings$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(userGetUserBookings, userLoginSuccess),
      switchMap(() =>
        this.userService.getUserBookings().pipe(
          map((response) => {
            return userGetUserBookingsSuccess({ payload: response.data });
          }),
        ),
      ),
    );
  });

  updatePersonalInfo$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(userUpdatePersonalInfo),
      mergeMap((action) => {
        return this.userService.updateUserPersonalInfo(action.payload).pipe(
          switchMap(() => of(userUpdatePersonalInfoSuccess())),
          catchError(() =>
            of(
              onFormError({
                error: new BaseError(ErrorType.UserUpdateFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  updateTravelPreferences$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(userUpdateTravelPreferences),
      mergeMap((action) => {
        return this.userService.updateUserPersonalInfo(action.payload).pipe(
          switchMap(() => of(userUpdateTravelPreferencesSuccess())),
          catchError(() =>
            of(
              onFormError({
                error: new BaseError(ErrorType.UserUpdateFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  updateContactInfo$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(userUpdateContactInfo),
      mergeMap((action) => {
        return forkJoin([
          this.userService.updateUserEmailAddress(
            action.emailKey,
            action.emailUpdateRequest,
          ),
          this.userService.updateUserPhoneNumber(
            action.phoneKey,
            action.phoneUpdateRequest,
          ),
        ]).pipe(
          switchMap(() => of(userUpdateContactInfoSuccess())),
          catchError(() =>
            of(
              onFormError({
                error: new BaseError(ErrorType.UserUpdateFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  updateUserTravelDocuments$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(userUpdateTravelDocuments),
      mergeMap((action) => {
        if (
          action.createRequests.length === 0 &&
          action.updateRequests.length === 0
        ) {
          return of(appNoOp());
        }

        return forkJoin([
          ...action.updateRequests.map((r) =>
            this.userService.updateUserTravelDocument(
              r.documentUpdateRequest,
              r.docKey,
            ),
          ),
          ...action.createRequests.map((r) =>
            this.userService.createUserTravelDocument(r),
          ),
        ]).pipe(
          switchMap(() => of(userUpdateTravelDocumentsSuccess())),
          catchError(() =>
            of(
              onFormError({
                error: new BaseError(ErrorType.UserUpdateFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  resetPassword$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(userResetPassword),
      concatLatestFrom(() =>
        this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM)),
      ),
      mergeMap(([{ username }, culture]) => {
        if (!culture) {
          return of(appNoOp());
        }

        return this.userService.resetPassword(username, culture).pipe(
          switchMap(() => of(userResetPasswordSuccess())),
          catchError(() =>
            of(
              onFormError({
                error: new BaseError(ErrorType.ResetPasswordValidationFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  completeResetPassword$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(userCompleteResetPassword),
      mergeMap(({ newpassword, resetToken }) => {
        return this.userService
          .completeResetPassword(newpassword, resetToken)
          .pipe(
            switchMap(({ data: user }) =>
              of(userCompleteResetPasswordSuccess({ user })),
            ),
            catchError(() =>
              of(
                onFormError({
                  error: new BaseError(
                    ErrorType.CompleteResetPasswordValidationFailure,
                  ),
                }),
              ),
            ),
          );
      }),
    );
  });

  signUp$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(userSignUp),
      mergeMap((formData) => {
        const data = this.userService.transformFormDataToCreateUserRequest(
          formData as unknown as SignUpForm,
        );
        return this.userService.createUser(data).pipe(
          switchMap(({ data: user }) => of(userLoginSuccess({ user }))),
          catchError(() =>
            of(
              onFormError({
                error: new BaseError(ErrorType.SignUpValidationFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  loginSuccess$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(userLoginSuccess),
        tap((action) => {
          const emailAddress = action.user.loginResponse?.emailAddress;
          if (
            emailAddress &&
            this.cookieVerifyService.shouldVerify(emailAddress)
          ) {
            this.cookieVerifyService.createVerifyCookie(emailAddress);
          }
        }),
      );
    },
    { dispatch: false },
  );
}
